import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextInput from "../../../components/inputs/TextInput";
import useAuthStore from "../../../store/Auth";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { fileToBase64, mapToOptions } from "../../../lib/functions";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllMusterPoints,
  useGetMusterPointById,
} from "../../../hooks/common/query";
import {
  useAddUpdateMusterPoint,
  useGetMusterPointFile,
} from "../../../hooks/common/mutation";
import {
  allowedImagesTypes,
  ATTACHMENTS_TYPE,
  ContentType,
  PAGE_SIZE,
  SelectInputOption,
} from "../../../constants/common";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";
import { MusterPointsConfigurationSchema } from "../../../schemas/musterPointsConfigurationSchema";
import SelectInput from "../../../components/inputs/SelectInput";
import MusterPointsConfigurationTable from "../../../components/table/MusterPointsConfigurationTable";

interface MusterPointsConfigurationData {
  musterPointNameEn: string;
  musterPointNameAr: string;
  location: string;
  description?: string | null;
  musterPointLink: string;
  musterPointPhoto?: any;
}
const MusterPointsConfiguration = () => {
  const { t, i18n } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [havePhoto, setHavePhoto] = useState(0);

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
    watch,
  } = useFormWithSchema(MusterPointsConfigurationSchema, {
    context: { havePhoto: !!havePhoto },
  });

  const [selectedMusterPointId, setSelectedMusterPointId] = useState(0);

  const uploadPhotoRef = useRef<HTMLInputElement | null>(null);

  const [photoFileName, setPhotoFileName] = useState("");
  const [selectedType, setSelectedType] = useState(0);
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);

  const [musterPoints, setMusterPoints] = useState([]);

  const { data: allLocations } = useGetAllLocationsDropdown();

  const { data: allMusterPoints } = useGetAllMusterPoints({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (allMusterPoints) {
      setMusterPoints(allMusterPoints.data || []);
      setPages(allMusterPoints.pagination?.pages || 1);
      setCount(allMusterPoints.pagination?.count || 0);
    }
  }, [setPages, allMusterPoints]);

  const {
    mutateAsync: AddUpdateMusterPoint,
    isPending: isAddUpdateMusterPointLoading,
  } = useAddUpdateMusterPoint();

  const {
    mutateAsync: GetMusterPointFile,
    isPending: isGetMusterPointFileLoading,
  } = useGetMusterPointFile();

  const user = useAuthStore((state) => state.user);

  const { data: selectedMusterPoint } = useGetMusterPointById({
    encryptedId: selectedMusterPointId ? selectedMusterPointId.toString() : "",
  });

  const [selectedBuildingsOptions, setSelectedBuildingsOptions] = useState<
    SelectInputOption[]
  >([]);

  useEffect(() => {
    if (selectedMusterPoint) {
      setSelectedMusterPointId(selectedMusterPoint.n_MusterPointID);
      setValue("musterPointNameEn", selectedMusterPoint.s_MusterPointName_En);
      setValue("musterPointNameAr", selectedMusterPoint.s_MusterPointName_Ar);
      setValue("location", selectedMusterPoint.n_LocationID);
      setValue("musterPointLink", selectedMusterPoint.s_MusterPointMapLink);

      setHavePhoto(selectedMusterPoint.haveMusterPointMap);

      setSelectedBuildingsOptions(
        selectedMusterPoint.buildings.map((item: any) => ({
          label:
            i18n.language === "en"
              ? item.s_BuildingName_En
              : item.s_BuildingName_Ar,
          value: item.n_BuildingID,
          color: "#403f68",
        })),
      );
    }
  }, [i18n.language, selectedMusterPoint, setValue]);

  const selectedLocationId = watch("location");

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings?.map(
        (item: {
          n_BuildingID: string;
          s_BuildingName_En: string;
          s_BuildingName_Ar: string;
        }) => ({
          value: item.n_BuildingID,
          label:
            i18n.language === "en"
              ? item.s_BuildingName_En
              : item.s_BuildingName_Ar,
          color: "#403f68",
        }),
      ),
    [allBuildings, i18n.language],
  );

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        if (selectedMusterPointId) {
          setSelectedType(type);
          const body = {
            id: selectedMusterPointId,
            type: "1",
          };

          const res = await GetMusterPointFile(body);
          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        setSelectedType(0);
      }
    },
    [GetMusterPointFile, selectedMusterPointId, t],
  );

  const onClear = useCallback(() => {
    setSelectedMusterPointId(0);

    resetField("musterPointNameAr");
    resetField("musterPointNameEn");
    resetField("location");
    resetField("musterPointLink");
    resetField("musterPointPhoto");

    setPhotoFileName("");

    setHavePhoto(0);

    setSelectedBuildingsOptions([]);

    if (uploadPhotoRef.current) {
      uploadPhotoRef.current.value = "";
    }
  }, [resetField]);

  const onSubmit = useCallback(
    async (data: MusterPointsConfigurationData) => {
      if (selectedBuildingsOptions.length === 0) {
        toast.error(t("kindlySelectAtLeastOneBuilding"));
        return;
      }

      try {
        if (user?.user_id) {
          const body = {
            n_MusterPointID: selectedMusterPointId || 0,
            s_MusterPointName_En: data.musterPointNameEn,
            s_MusterPointName_Ar: data.musterPointNameAr,
            n_LocationID: Number(data.location),
            s_MusterPointMapLink: data.musterPointLink,
            buildings: selectedBuildingsOptions.map((item) => ({
              n_BuildingID: Number(item.value),
            })),
            s_MusterPointMapFile: data.musterPointPhoto
              ? await fileToBase64(data.musterPointPhoto)
              : "",
            s_MusterPointMapFileContentType: data.musterPointPhoto
              ? data.musterPointPhoto.type
              : "",
          };
          if (!selectedMusterPointId) {
            await AddUpdateMusterPoint(body);
          } else {
            const updateBody = {
              ...body,
              n_MusterPointID: selectedMusterPointId,
            };
            await AddUpdateMusterPoint(updateBody);
          }

          if (!selectedMusterPointId) {
            toast.success(t("musterPointAdded"));
          } else {
            toast.success(t("musterPointUpdated"));
          }
        }

        onClear();
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [
      selectedBuildingsOptions,
      t,
      user?.user_id,
      onClear,
      selectedMusterPointId,
      AddUpdateMusterPoint,
    ],
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("musterPoints")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)] flex flex-col gap-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 ">
              <Controller
                name="musterPointNameEn"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("musterPointNameEn")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                    required
                  />
                )}
              />

              <Controller
                name="musterPointNameAr"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("musterPointNameAr")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              <Controller
                name="musterPointLink"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("musterPointLink")}
                    onChange={onChange}
                    value={value || ""}
                    required
                    error={errors?.[name]?.message}
                  />
                )}
              />

              <FileUploadInput
                name="musterPointPhoto"
                label={t("musterPointPhoto")}
                accept=".png,.jpg,.jpeg"
                allowedTypes={allowedImagesTypes}
                isAttachments={havePhoto}
                control={control}
                uploadRef={uploadPhotoRef}
                fileName={photoFileName}
                setFileName={setPhotoFileName}
                handleAttachmentView={handleAttachmentView}
                selectedType={selectedType}
                attachmentType={ATTACHMENTS_TYPE.PHOTO}
                isGetFileByTypeLoading={isGetMusterPointFileLoading}
                error={errors.musterPointPhoto}
                S_FileFieldName="musterPointPhoto"
                s_FiledFileType="_"
                onRemoveAttachment={() => setHavePhoto(0)}
              />

              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                    required
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />
            </div>

            <SelectInput
              label={t("buildings")}
              options={buildings}
              required
              selectedOptions={selectedBuildingsOptions}
              setSelectedOptions={setSelectedBuildingsOptions}
            />

            <div className="flex items-center gap-2">
              <Button isLoading={isAddUpdateMusterPointLoading}>
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>

          <MusterPointsConfigurationTable
            musterPoints={musterPoints}
            setSelectedMusterPointId={setSelectedMusterPointId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default MusterPointsConfiguration;
